import { useEffect, useState } from 'react'; // Remove useContext
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { RootState } from '../store/store';
import { flashcardsService } from '../services/flashcardsService';
import { collectionsService } from '../services/collectionsService';
import { setFlashcards, addFlashcard, updateFlashcard, removeFlashcard } from '../store/flashcardsSlice';
import { useTheme } from '../context/ThemeContext'; // Import useTheme instead of ThemeContext
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface FlashcardFormData {
  question: string;
  options: string[];
  correctAnswer: number;
}

export const CollectionManage = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { darkMode } = useTheme(); // Use useTheme hook and get darkMode boolean
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collection, setCollection] = useState<any>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const flashcards = useSelector((state: RootState) => state.flashcards.flashcards) || [];
  const [formData, setFormData] = useState<FlashcardFormData>({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [collectionData, flashcardsData] = await Promise.all([
          collectionsService.getCollection(id!),
          flashcardsService.getFlashcards(id!),
        ]);
        setCollection(collectionData);
        dispatch(setFlashcards(flashcardsData));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, dispatch]);

  const handleCreateFlashcard = async () => {
    try {
      const flashcard = await flashcardsService.createFlashcard(id!, formData);
      dispatch(addFlashcard(flashcard));
      setFormData({
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
      });
      setIsCreating(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create flashcard');
    }
  };

  const handleUpdateFlashcard = async (flashcardId: string) => {
    try {
      const flashcard = await flashcardsService.updateFlashcard(flashcardId, formData);
      dispatch(updateFlashcard(flashcard));
      setEditingId(null);
      setFormData({
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update flashcard');
    }
  };

  const handleDeleteFlashcard = async (flashcardId: string) => {
    if (window.confirm('Are you sure you want to delete this flashcard?')) {
      try {
        await flashcardsService.deleteFlashcard(flashcardId);
        dispatch(removeFlashcard(flashcardId));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete flashcard');
      }
    }
  };

  const startEditing = (flashcard: any) => {
    setEditingId(flashcard._id);
    setFormData({
      question: flashcard.question,
      options: flashcard.options,
      correctAnswer: flashcard.correctAnswer,
    });
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-b from-gray-50 to-white'} relative overflow-hidden`}>
      {/* Background Elements - Adjust or remove for dark mode if needed */}
      <div className={`absolute inset-0 z-0 ${darkMode ? 'opacity-20' : ''}`}>
        <div className={`absolute inset-0 ${darkMode ? 'bg-[radial-gradient(ellipse_at_top,theme(colors.indigo.900),transparent)]' : 'bg-[radial-gradient(ellipse_at_top,theme(colors.indigo.50),transparent)]'}`}></div>
        <div className={`absolute inset-0 ${darkMode ? 'bg-[radial-gradient(ellipse_at_bottom,theme(colors.purple.900),transparent)]' : 'bg-[radial-gradient(ellipse_at_bottom,theme(colors.purple.50),transparent)]'}`}></div>
        <div className={`absolute inset-0 ${darkMode ? 'hidden' : ''}`} style={{
          backgroundImage: 'radial-gradient(circle at center, #f3f4f6 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          opacity: 0.3
        }}></div>
      </div>

      <NavigationBar />
      <div className="pt-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${darkMode ? 'border-indigo-400' : 'border-indigo-500'}`}></div>
            </div>
          ) : (
            <div>
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{collection?.title}</h1>
                  <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{collection?.description}</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setIsCreating(true)}
                  className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${darkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800' : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700'}`}
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Flashcard
                </motion.button>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-red-900/50 border border-red-700 text-red-300' : 'bg-red-50 border border-red-200 text-red-600'}`}
                >
                  {error}
                </motion.div>
              )}

              <AnimatePresence mode="popLayout">
                {(isCreating || editingId) && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={`mb-8 p-6 rounded-xl shadow-lg ${darkMode ? 'bg-gray-800/80 backdrop-blur-lg border border-gray-700/50' : 'bg-white/60 backdrop-blur-lg border border-gray-100/50'}`}
                  >
                    <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {isCreating ? 'Create New Flashcard' : 'Edit Flashcard'}
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Question
                        </label>
                        <input
                          type="text"
                          value={formData.question}
                          onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                          className={`w-full px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600 text-white focus:border-indigo-400 focus:ring-indigo-300/50' : 'bg-white/50 border-gray-300 focus:border-indigo-500 focus:ring-indigo-200'}`}
                          placeholder="Enter your question"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Options</label>
                        {formData.options && formData.options.map((option, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <input
                              type="radio"
                              checked={formData.correctAnswer === index}
                              onChange={() => setFormData({ ...formData, correctAnswer: index })}
                              className={`h-4 w-4 ${darkMode ? 'text-indigo-400 bg-gray-600 border-gray-500 focus:ring-indigo-400/50 focus:ring-offset-gray-800' : 'text-indigo-600 border-gray-300 focus:ring-indigo-500'}`}
                            />
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...formData.options];
                                newOptions[index] = e.target.value;
                                setFormData({ ...formData, options: newOptions });
                              }}
                              className={`flex-1 px-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-700/50 border-gray-600 text-white focus:border-indigo-400 focus:ring-indigo-300/50' : 'bg-white/50 border-gray-300 focus:border-indigo-500 focus:ring-indigo-200'}`}
                              placeholder={`Option ${index + 1}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end space-x-3">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setIsCreating(false);
                            setEditingId(null);
                            setFormData({
                              question: '',
                              options: ['', '', '', ''],
                              correctAnswer: 0,
                            });
                          }}
                          className={`px-4 py-2 border rounded-md text-sm font-medium ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            if (editingId) {
                              handleUpdateFlashcard(editingId);
                            } else {
                              handleCreateFlashcard();
                            }
                          }}
                          className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${darkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800' : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700'}`}
                        >
                          {editingId ? 'Update' : 'Create'} Flashcard
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {flashcards && flashcards.map((flashcard) => (
                    <motion.div
                      key={flashcard._id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className={`rounded-xl shadow-lg overflow-hidden ${darkMode ? 'bg-gray-800/80 backdrop-blur-lg border border-gray-700/50' : 'bg-white/60 backdrop-blur-lg border border-gray-100/50'}`}
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between">
                          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {flashcard.question}
                          </h3>
                          <div className="flex space-x-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => startEditing(flashcard)}
                              className={`p-1 ${darkMode ? 'text-gray-400 hover:text-indigo-400' : 'text-gray-400 hover:text-indigo-600'}`}
                            >
                              <PencilSquareIcon className="h-5 w-5" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeleteFlashcard(flashcard._id)}
                              className={`p-1 ${darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-400 hover:text-red-600'}`}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </motion.button>
                          </div>
                        </div>
                        <div className="mt-4 space-y-2">
                          {flashcard.options && flashcard.options.map((option, index) => (
                            <div
                              key={index}
                              className={`flex items-center p-2 rounded-lg border ${
                                index === flashcard.correctAnswer
                                  ? darkMode ? 'bg-green-900/50 text-green-300 border-green-700' : 'bg-green-50 text-green-700 border-green-200'
                                  : darkMode ? 'bg-gray-700/50 text-gray-300 border-gray-600' : 'bg-gray-50 text-gray-700 border-gray-200'
                              }`}
                            >
                              {index === flashcard.correctAnswer && (
                                <CheckCircleIcon className={`h-5 w-5 mr-2 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
                              )}
                              <span>{option}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};